"use client";

import { ReactNode } from "react";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";

import { GROQButton } from "@/sanity/lib/definitions";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { PipeChip } from "../home/_shared";

import { ArchesDesktop, ArchesMobile } from "./ArchesBackdrop";
import HeroCountdown from "./HeroCountdown";

interface FinalCtaSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  cta?: GROQButton;
  eventDate?: string;
  eventDateText?: string;
  /** Rendered inside the section so it sits on the render, not on black. */
  footer?: ReactNode;
}

// Final CTA (Figma 5143:2210 desktop, 5143:2542 mobile) — chip, serif
// headline (em → italic), body, CTA and the white countdown card over the
// arches render (see ArchesBackdrop): the desktop plate is pinned 309px down
// the 1048px-tall band and fades to black at both ends; the mobile plate flows
// between the card and the footer. The footer bar closes the band on both.
const DESKTOP_WASH =
  "linear-gradient(180deg, rgba(0,0,0,0) 68.9%, #000 81.4%), linear-gradient(179.4deg, #000 1.6%, rgba(0,0,0,0.87) 11.8%, rgba(0,0,0,0) 33.4%)";

export default function FinalCtaSection({ chip, title, cta, eventDate, eventDateText, footer }: FinalCtaSectionProps) {
  return (
    <section className="relative flex w-full flex-col items-center overflow-hidden bg-black lg:h-[131rem] lg:justify-between">
      <ArchesDesktop
        className="top-[38.625rem]"
        wash={DESKTOP_WASH}
      />

      <div className="relative z-10 flex w-full flex-col items-center gap-[3rem] px-[2.625rem] pt-[9rem] lg:gap-[4rem] lg:px-0 lg:pt-0">
        <div className="flex w-[min(45.5rem,100%)] flex-col items-center gap-[3rem] lg:w-auto">
          <div className="flex flex-col items-center gap-[2rem] text-center">
            {chip && (
              <FadeIn delay={0}>
                <PipeChip>{chip}</PipeChip>
              </FadeIn>
            )}

            {title && (
              <FadeIn
                delay={0.1}
                className="flex w-full flex-col items-center gap-[1.25rem]">
                <PortableText
                  value={title}
                  components={customRichTextComponent({
                    block: {
                      h1: ({ children }) => (
                        <h2 className="mx-auto mb-0 text-center font-news text-[4.5rem] leading-[1.1] font-normal tracking-[-0.21rem] whitespace-nowrap text-white lg:max-w-[125.75rem] lg:text-[7rem] lg:leading-[1.08]">
                          {children}
                        </h2>
                      ),
                      normal: ({ children }) => (
                        <p className="mx-auto mb-0 max-w-[42rem] text-center font-geist text-[2.25rem] leading-[1.378] text-white/60 lg:max-w-[55.5rem] lg:text-white/70">
                          {children}
                        </p>
                      ),
                    },
                    marks: {
                      em: ({ children }) => <em className="font-news italic">{children}</em>,
                    },
                  })}
                />
              </FadeIn>
            )}
          </div>

          {cta?.cta && (
            <FadeIn
              delay={0.2}
              className="w-full lg:w-auto">
              <CTAButton data={cta} />
            </FadeIn>
          )}
        </div>

        <FadeIn
          delay={0.3}
          className="w-full lg:w-[71.75rem]">
          <HeroCountdown
            date={eventDate}
            caption={eventDateText}
          />
        </FadeIn>
      </div>

      {/* Mobile plate — flows between the card and the footer. */}
      <ArchesMobile />

      {footer && <div className="relative z-10 w-full">{footer}</div>}
    </section>
  );
}
